/**
 * Prozedural erzeugte Low-Poly-3D-Modelle (glTF 2.0) für die fünf
 * Flugzeugklassen (Airliner, Privatjet, Kleinflugzeug, Segelflugzeug,
 * Helikopter) — komplett im Code gebaut, keine Binär-Assets im Repo.
 *
 * Authoring-Konvention (glTF-Koordinaten): Nase entlang +Z, Oberseite +Y,
 * Flügel entlang ±X — das ist die glTF-Spezifikation ("front of asset is +Z").
 * Cesium richtet solche Modelle automatisch so aus, dass +Z(glTF) → +X(Cesium,
 * "forward") und +Y(glTF) → +Z(Cesium, "up") zeigt. Die Heading-Korrektur
 * (−90°) übernimmt der FlightManager.
 *
 * Alle Maße in Metern (grob realistische Proportionen), die sichtbare Größe
 * regelt `minimumPixelSize` am Entity-Model.
 */

// ---------------------------------------------------------------------------
// Geometrie-Builder: Dreiecke mit Flat-Shading (Vertex-Duplikate pro Fläche).
// Windings werden automatisch so orientiert, dass die Normale von einem
// Referenzpunkt (Formzentrum) weg zeigt — erspart fehleranfällige Handarbeit.
// ---------------------------------------------------------------------------

class Geo {
  constructor() {
    this.positions = [];
    this.normals = [];
    this.indices = [];
  }

  tri(a, b, c, outwardRef) {
    let n = normal(a, b, c);
    if (outwardRef) {
      const cx = (a[0] + b[0] + c[0]) / 3 - outwardRef[0];
      const cy = (a[1] + b[1] + c[1]) / 3 - outwardRef[1];
      const cz = (a[2] + b[2] + c[2]) / 3 - outwardRef[2];
      if (n[0] * cx + n[1] * cy + n[2] * cz < 0) {
        const t = b; b = c; c = t;
        n = [-n[0], -n[1], -n[2]];
      }
    }
    const base = this.positions.length / 3;
    for (const p of [a, b, c]) {
      this.positions.push(p[0], p[1], p[2]);
      this.normals.push(n[0], n[1], n[2]);
    }
    this.indices.push(base, base + 1, base + 2);
  }

  quad(a, b, c, d, outwardRef) {
    // Flip einmal fürs ganze Quad entscheiden, damit beide Dreiecke
    // dieselbe Orientierung (und Normale) bekommen
    let n = normal(a, b, c);
    if (outwardRef) {
      const cx = (a[0] + b[0] + c[0] + d[0]) / 4 - outwardRef[0];
      const cy = (a[1] + b[1] + c[1] + d[1]) / 4 - outwardRef[1];
      const cz = (a[2] + b[2] + c[2] + d[2]) / 4 - outwardRef[2];
      if (n[0] * cx + n[1] * cy + n[2] * cz < 0) {
        const t = b; b = d; d = t;
      }
    }
    this.tri(a, b, c, null);
    this.tri(a, c, d, null);
  }
}

function normal(a, b, c) {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz) || 1;
  return [nx / len, ny / len, nz / len];
}

/**
 * Ringpunkte (x, y) eines Querschnitts: 12-Eck (wirkt schon rund, flache
 * Ober-/Unterseite durch den halben Winkel-Offset) oder explizites n-Eck.
 */
function ringPoints(sec) {
  const n = sec.n || 12;
  const offset = Math.PI / n; // flache Ober-/Unterseite
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * 2 * Math.PI + offset;
    pts.push([sec.rx * Math.sin(a), sec.cy + sec.ry * Math.cos(a)]);
  }
  return pts;
}

/**
 * Rumpf-Loft entlang der Längsachse (+Z = Nase). Jede Section:
 * { z, cy, rx, ry, n? } — Zentrum (0, cy, z), Halbbreite rx, Halbhöhe ry.
 */
function loft(geo, sections) {
  const rings = sections.map((s) =>
    ringPoints(s).map(([x, y]) => [x, y, s.z])
  );
  const count = rings[0].length;

  for (let i = 0; i < rings.length - 1; i++) {
    const r0 = rings[i];
    const r1 = rings[i + 1];
    const ref = [
      0,
      (sections[i].cy + sections[i + 1].cy) / 2,
      (sections[i].z + sections[i + 1].z) / 2
    ];
    for (let k = 0; k < count; k++) {
      const k2 = (k + 1) % count;
      geo.quad(r0[k], r0[k2], r1[k2], r1[k], ref);
    }
  }

  // Endkappen (Normale zeigt in ±Z, vom jeweils anderen Ende weg)
  for (const [ring, sec, other] of [
    [rings[0], sections[0], sections[1]],
    [rings[rings.length - 1], sections[sections.length - 1], sections[sections.length - 2]]
  ]) {
    const dir = Math.sign(sec.z - other.z) || 1;
    const center = [0, sec.cy, sec.z];
    const ref = [0, sec.cy, sec.z - dir];
    for (let k = 0; k < count; k++) {
      geo.tri(center, ring[k], ring[(k + 1) % count], ref);
    }
  }
}

/**
 * Flache Platte (Flügel/Leitwerk): 4 Eckpunkte auf der Mittelebene
 * (Reihenfolge entlang des Umfangs), extrudiert um `t` entlang `up`.
 */
function slab(geo, corners, up, t) {
  const h = t / 2;
  const top = corners.map((p) => [p[0] + up[0] * h, p[1] + up[1] * h, p[2] + up[2] * h]);
  const bot = corners.map((p) => [p[0] - up[0] * h, p[1] - up[1] * h, p[2] - up[2] * h]);
  const ref = [
    corners.reduce((s, p) => s + p[0], 0) / 4,
    corners.reduce((s, p) => s + p[1], 0) / 4,
    corners.reduce((s, p) => s + p[2], 0) / 4
  ];
  geo.quad(top[0], top[1], top[2], top[3], ref);
  geo.quad(bot[0], bot[1], bot[2], bot[3], ref);
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    geo.quad(top[i], top[j], bot[j], bot[i], ref);
  }
}

/** Achsparalleler Quader, Zentrum (cx, cy, cz), volle Kantenlängen s*. */
function box(geo, cx, cy, cz, sx, sy, sz) {
  slab(geo, [
    [cx - sx / 2, cy, cz - sz / 2],
    [cx + sx / 2, cy, cz - sz / 2],
    [cx + sx / 2, cy, cz + sz / 2],
    [cx - sx / 2, cy, cz + sz / 2]
  ], [0, 1, 0], sy);
}

/**
 * Dünne Scheibe (Rotor/Propeller) mit Radius r und Dicke t um `center`,
 * Achse 'y' (Hauptrotor), 'x' (Heckrotor) oder 'z' (Propeller).
 */
function disc(geo, center, axis, r, t, n = 18) {
  const [cx, cy, cz] = center;
  const place = (u, v, off) => {
    if (axis === 'y') return [cx + u, cy + off, cz + v];
    if (axis === 'x') return [cx + off, cy + u, cz + v];
    return [cx + u, cy + v, cz + off]; // 'z'
  };
  const rings = [[], []];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * 2 * Math.PI;
    const u = r * Math.cos(a);
    const v = r * Math.sin(a);
    rings[0].push(place(u, v, -t / 2));
    rings[1].push(place(u, v, t / 2));
  }
  for (let k = 0; k < n; k++) {
    const k2 = (k + 1) % n;
    geo.quad(rings[0][k], rings[0][k2], rings[1][k2], rings[1][k], center);
  }
  for (const [ring, sign] of [[rings[0], -1], [rings[1], 1]]) {
    const capCenter = place(0, 0, sign * t / 2);
    for (let k = 0; k < n; k++) {
      geo.tri(capCenter, ring[k], ring[(k + 1) % n], center);
    }
  }
}

/**
 * Flügelpaar (beidseitig gespiegelt). Alle x/z in Metern:
 * root/tip = { le, te } (Vorder-/Hinterkante in z), Spannweite über spanX,
 * Höhen rootY/tipY (Dihedral), Dicke t.
 */
function wingPair(geo, { root, tip, rootX, spanX, rootY, tipY, t }) {
  for (const side of [1, -1]) {
    slab(geo, [
      [side * rootX, rootY, root.le],
      [side * spanX, tipY, tip.le],
      [side * spanX, tipY, tip.te],
      [side * rootX, rootY, root.te]
    ], [0, 1, 0], t);
  }
}

/** Seitenleitwerk: Punkte in (z, y), Dicke in x. */
function fin(geo, pts, t) {
  slab(geo, pts.map(([z, y]) => [0, y, z]), [1, 0, 0], t);
}

// ---------------------------------------------------------------------------
// glTF-Assembly
// ---------------------------------------------------------------------------

const MAT = {
  body: {
    color: [0.95, 0.96, 1.0, 1.0],
    metallic: 0.05, roughness: 0.55, emissive: [0.10, 0.10, 0.11]
  },
  dark: {
    color: [0.16, 0.17, 0.20, 1.0],
    metallic: 0.1, roughness: 0.6, emissive: [0.02, 0.02, 0.02]
  },
  glass: {
    color: [0.10, 0.17, 0.30, 1.0],
    metallic: 0.3, roughness: 0.25, emissive: [0.02, 0.04, 0.08]
  },
  rotor: {
    color: [0.10, 0.10, 0.12, 0.45],
    metallic: 0.0, roughness: 0.8, emissive: [0.01, 0.01, 0.01],
    blend: true, doubleSided: true
  }
};

function toBase64(bytes) {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/**
 * Baut aus [{ geo, mat }] ein komplettes glTF (JSON + eingebetteter Buffer)
 * und liefert eine Blob-URL, die Cesium direkt als model.uri laden kann.
 */
function buildGltf(parts) {
  const bufferViews = [];
  const accessors = [];
  const materials = [];
  const primitives = [];
  const chunks = [];
  let byteOffset = 0;

  const pushChunk = (typedArray, target) => {
    const bytes = new Uint8Array(typedArray.buffer, 0, typedArray.byteLength);
    chunks.push(bytes);
    const view = { buffer: 0, byteOffset, byteLength: bytes.byteLength, target };
    byteOffset += bytes.byteLength;
    // 4-Byte-Alignment für den nächsten View
    const pad = (4 - (byteOffset % 4)) % 4;
    if (pad) {
      chunks.push(new Uint8Array(pad));
      byteOffset += pad;
    }
    bufferViews.push(view);
    return bufferViews.length - 1;
  };

  for (const { geo, mat } of parts) {
    const pos = new Float32Array(geo.positions);
    const nor = new Float32Array(geo.normals);
    const idx = new Uint16Array(geo.indices);

    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < pos.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        if (pos[i + k] < min[k]) min[k] = pos[i + k];
        if (pos[i + k] > max[k]) max[k] = pos[i + k];
      }
    }

    const posView = pushChunk(pos, 34962);
    const norView = pushChunk(nor, 34962);
    const idxView = pushChunk(idx, 34963);

    accessors.push(
      { bufferView: posView, componentType: 5126, count: pos.length / 3, type: 'VEC3', min, max },
      { bufferView: norView, componentType: 5126, count: nor.length / 3, type: 'VEC3' },
      { bufferView: idxView, componentType: 5123, count: idx.length, type: 'SCALAR' }
    );

    materials.push({
      pbrMetallicRoughness: {
        baseColorFactor: mat.color,
        metallicFactor: mat.metallic,
        roughnessFactor: mat.roughness
      },
      emissiveFactor: mat.emissive,
      ...(mat.blend ? { alphaMode: 'BLEND' } : {}),
      ...(mat.doubleSided ? { doubleSided: true } : {})
    });

    const base = accessors.length - 3;
    primitives.push({
      attributes: { POSITION: base, NORMAL: base + 1 },
      indices: base + 2,
      material: materials.length - 1
    });
  }

  const bin = new Uint8Array(byteOffset);
  let off = 0;
  for (const c of chunks) {
    bin.set(c, off);
    off += c.byteLength;
  }

  const gltf = {
    asset: { version: '2.0', generator: 'str-flyradar-procedural' },
    scene: 0,
    scenes: [{ nodes: [0] }],
    nodes: [{ mesh: 0 }],
    meshes: [{ primitives }],
    materials,
    accessors,
    bufferViews,
    buffers: [{
      byteLength: byteOffset,
      uri: `data:application/octet-stream;base64,${toBase64(bin)}`
    }]
  };

  return URL.createObjectURL(
    new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' })
  );
}

// ---------------------------------------------------------------------------
// Die vier Flugzeugklassen
// ---------------------------------------------------------------------------

/** Verkehrsflugzeug (~A320): Pfeilflügel, Triebwerke, Winglets, Seitenleitwerk. */
function buildAirliner() {
  const body = new Geo();
  const glass = new Geo();

  // Rumpf: Nase +Z, weich gerundete Nase, Heck zieht nach oben
  loft(body, [
    { z: 18.9, cy: -0.3, rx: 0.16, ry: 0.14 },
    { z: 18.0, cy: -0.2, rx: 0.8, ry: 0.75 },
    { z: 16.4, cy: -0.08, rx: 1.4, ry: 1.35 },
    { z: 14.2, cy: 0.0, rx: 1.82, ry: 1.78 },
    { z: 11.0, cy: 0.0, rx: 2.0, ry: 2.0 },
    { z: -8.0, cy: 0.0, rx: 2.0, ry: 2.0 },
    { z: -13.0, cy: 0.45, rx: 1.55, ry: 1.55 },
    { z: -16.5, cy: 1.05, rx: 0.95, ry: 0.95 },
    { z: -18.5, cy: 1.5, rx: 0.38, ry: 0.42 }
  ]);

  // Cockpitfenster: dunkles Band vorn oben
  box(glass, 0, 0.95, 15.6, 3.2, 0.9, 1.7);

  // Pfeilflügel (Tiefflügler, leichte V-Stellung)
  wingPair(body, {
    root: { le: 4.5, te: -2.5 },
    tip: { le: -2.0, te: -4.5 },
    rootX: 1.2, spanX: 17.0,
    rootY: -0.9, tipY: 1.3, t: 0.5
  });

  // Winglets (A320neo-Look: an den Flügelspitzen nach oben gezogen)
  for (const side of [1, -1]) {
    slab(body, [
      [side * 16.7, 1.25, -2.1],
      [side * 17.15, 3.3, -3.7],
      [side * 17.15, 3.3, -4.55],
      [side * 16.7, 1.25, -4.45]
    ], [1, 0, 0], 0.16);
  }

  // Höhenleitwerk + Seitenleitwerk
  // (Triebwerke werden in createAircraftModelUris() gebaut und seitlich
  //  unter die Flügel versetzt — loft() arbeitet immer um x = 0)
  wingPair(body, {
    root: { le: -15.0, te: -18.0 },
    tip: { le: -17.3, te: -18.8 },
    rootX: 0.6, spanX: 6.4,
    rootY: 1.3, tipY: 1.7, t: 0.3
  });
  fin(body, [
    [-13.5, 1.6], [-18.2, 1.6], [-18.9, 7.6], [-16.6, 7.6]
  ], 0.4);

  return { body, glass };
}

/** Privatjet (~Citation/Phenom): schlanker Rumpf, T-Leitwerk, Hecktriebwerke. */
function buildBizjet() {
  const body = new Geo();
  const darkParts = new Geo();
  const glass = new Geo();

  // Schlanker Rumpf mit spitzer Nase und ansteigendem Heckkonus
  loft(body, [
    { z: 8.3, cy: -0.28, rx: 0.1, ry: 0.09 },
    { z: 7.5, cy: -0.16, rx: 0.5, ry: 0.48 },
    { z: 6.3, cy: 0.0, rx: 0.82, ry: 0.86 },
    { z: 4.5, cy: 0.05, rx: 0.95, ry: 1.0 },
    { z: -2.5, cy: 0.05, rx: 0.95, ry: 1.0 },
    { z: -5.5, cy: 0.35, rx: 0.58, ry: 0.62 },
    { z: -7.8, cy: 0.78, rx: 0.2, ry: 0.26 }
  ]);

  // Cockpitverglasung
  box(glass, 0, 0.6, 6.3, 1.55, 0.55, 1.3);

  // Tiefdecker mit leichter Pfeilung und V-Stellung
  wingPair(body, {
    root: { le: 1.8, te: -1.2 },
    tip: { le: -1.4, te: -2.6 },
    rootX: 0.6, spanX: 8.4,
    rootY: -0.75, tipY: 0.35, t: 0.3
  });

  // Winglets
  for (const side of [1, -1]) {
    slab(body, [
      [side * 8.2, 0.3, -1.55],
      [side * 8.45, 1.5, -2.3],
      [side * 8.45, 1.5, -2.72],
      [side * 8.2, 0.3, -2.55]
    ], [1, 0, 0], 0.12);
  }

  // Hecktriebwerke beidseitig am Rumpf (Nacelle + dunkler Fan vorn)
  for (const side of [1, -1]) {
    let before = body.positions.length / 3;
    loft(body, [
      { z: -1.9, cy: 0.35, rx: 0.46, ry: 0.46, n: 10 },
      { z: -2.6, cy: 0.35, rx: 0.5, ry: 0.5, n: 10 },
      { z: -4.6, cy: 0.35, rx: 0.32, ry: 0.32, n: 10 }
    ]);
    translateLast(body, body.positions.length / 3 - before, side * 1.35, 0, 0);
    disc(darkParts, [side * 1.35, 0.35, -1.92], 'z', 0.4, 0.08, 12);
    // Pylon Rumpf ↔ Triebwerk
    box(body, side * 1.0, 0.35, -3.2, 0.75, 0.5, 1.6);
  }

  // T-Leitwerk: Seitenflosse + Höhenleitwerk obenauf
  fin(body, [
    [-5.6, 0.7], [-7.7, 0.95], [-8.25, 3.1], [-7.1, 3.1]
  ], 0.25);
  wingPair(body, {
    root: { le: -6.9, te: -8.2 },
    tip: { le: -7.6, te: -8.4 },
    rootX: 0.15, spanX: 3.3,
    rootY: 3.1, tipY: 3.2, t: 0.18
  });

  return { body, darkParts, glass };
}

/** Kleinflugzeug (~Cessna): Schulterdecker, Propellerscheibe, kompakt. */
function buildSmallPlane() {
  const body = new Geo();
  const darkParts = new Geo();
  const glass = new Geo();

  loft(body, [
    { z: 4.0, cy: -0.1, rx: 0.5, ry: 0.55 },
    { z: 3.1, cy: 0.0, rx: 0.72, ry: 0.78 },
    { z: 1.2, cy: 0.1, rx: 0.85, ry: 0.9 },
    { z: -1.0, cy: 0.2, rx: 0.6, ry: 0.68 },
    { z: -3.9, cy: 0.55, rx: 0.2, ry: 0.3 }
  ]);

  // Windschutzscheibe
  box(glass, 0, 0.75, 2.1, 1.5, 0.75, 1.1);

  // Gerader Schulterdecker-Flügel (Markenzeichen der Cessna-Silhouette)
  wingPair(body, {
    root: { le: 1.5, te: -0.4 },
    tip: { le: 1.25, te: -0.25 },
    rootX: 0.5, spanX: 5.5,
    rootY: 1.0, tipY: 1.05, t: 0.22
  });

  // Propellerscheibe + Spinner an der Nase
  disc(darkParts, [0, -0.1, 4.15], 'z', 1.05, 0.08, 20);
  loft(darkParts, [
    { z: 4.1, cy: -0.1, rx: 0.28, ry: 0.28, n: 10 },
    { z: 4.55, cy: -0.1, rx: 0.06, ry: 0.06, n: 10 }
  ]);

  // Flügelstreben (Cessna-Markenzeichen: Diagonalstütze Rumpf ↔ Flügel)
  for (const side of [1, -1]) {
    slab(body, [
      [side * 0.6, -0.4, 1.35],
      [side * 3.1, 0.9, 1.2],
      [side * 3.18, 0.78, 1.05],
      [side * 0.7, -0.5, 1.2]
    ], [0, 0, 1], 0.09);
  }

  // Leitwerk
  wingPair(body, {
    root: { le: -3.1, te: -3.95 },
    tip: { le: -3.25, te: -3.95 },
    rootX: 0.2, spanX: 1.95,
    rootY: 0.55, tipY: 0.55, t: 0.15
  });
  fin(body, [
    [-2.6, 0.7], [-4.0, 0.7], [-4.15, 2.1], [-3.5, 2.1]
  ], 0.18);

  return { body, darkParts, glass };
}

/** Segelflugzeug (~ASK 21): schlanker Rumpf, sehr lange schmale Flügel, T-Leitwerk. */
function buildGlider() {
  const body = new Geo();
  const glass = new Geo();

  // Schlanker Rumpf mit spitzer Nase und langer, dünner Heckröhre
  loft(body, [
    { z: 3.6, cy: -0.15, rx: 0.1, ry: 0.12 },
    { z: 2.9, cy: -0.05, rx: 0.3, ry: 0.38 },
    { z: 1.6, cy: 0.05, rx: 0.38, ry: 0.5 },
    { z: 0.2, cy: 0.1, rx: 0.28, ry: 0.34 },
    { z: -3.6, cy: 0.25, rx: 0.12, ry: 0.16 },
    { z: -4.4, cy: 0.3, rx: 0.08, ry: 0.11 }
  ]);

  // Kabinenhaube (langgezogen, wie beim Doppelsitzer)
  box(glass, 0, 0.5, 1.7, 0.5, 0.35, 1.6);

  // Sehr lange, schmale Flügel (hohe Streckung) mit deutlicher V-Stellung
  wingPair(body, {
    root: { le: 0.95, te: 0.1 },
    tip: { le: 0.6, te: 0.15 },
    rootX: 0.28, spanX: 8.4,
    rootY: 0.15, tipY: 0.8, t: 0.12
  });

  // T-Leitwerk: schmale Flosse, Höhenleitwerk obenauf
  fin(body, [
    [-3.7, 0.3], [-4.45, 0.35], [-4.6, 1.75], [-4.15, 1.75]
  ], 0.14);
  wingPair(body, {
    root: { le: -4.05, te: -4.6 },
    tip: { le: -4.1, te: -4.55 },
    rootX: 0.05, spanX: 1.55,
    rootY: 1.75, tipY: 1.78, t: 0.1
  });

  return { body, glass };
}

/** Helikopter (~H135): Kabine, Heckausleger, große Rotorscheibe, Kufen. */
function buildHelicopter() {
  const body = new Geo();
  const darkParts = new Geo();
  const glass = new Geo();
  const rotor = new Geo();

  // Kabine
  loft(body, [
    { z: 3.4, cy: -0.3, rx: 0.3, ry: 0.35 },
    { z: 2.6, cy: -0.1, rx: 0.85, ry: 0.9 },
    { z: 1.0, cy: 0.1, rx: 1.1, ry: 1.15 },
    { z: -1.6, cy: 0.15, rx: 0.9, ry: 0.95 }
  ]);

  // Heckausleger
  loft(body, [
    { z: -1.3, cy: 0.45, rx: 0.42, ry: 0.45 },
    { z: -6.2, cy: 0.8, rx: 0.2, ry: 0.24 }
  ]);

  // Cockpitverglasung
  box(glass, 0, 0.35, 2.65, 1.5, 1.05, 1.2);

  // Seitenflosse + Heckrotor
  fin(body, [
    [-5.6, 0.5], [-6.6, 0.5], [-6.75, 2.3], [-6.15, 2.3]
  ], 0.25);
  disc(darkParts, [0.45, 1.45, -6.4], 'x', 0.85, 0.07, 12);

  // Höhenflosse am Heckausleger
  wingPair(body, {
    root: { le: -4.7, te: -5.3 },
    tip: { le: -4.75, te: -5.3 },
    rootX: 0.2, spanX: 1.5,
    rootY: 0.75, tipY: 0.78, t: 0.12
  });

  // Rotormast + große, halbtransparente Hauptrotorscheibe
  box(darkParts, 0, 1.5, 0.5, 0.5, 0.55, 0.6);
  disc(rotor, [0, 1.85, 0.5], 'y', 5.4, 0.07, 32);
  // Rotorkopf als sichtbarer Kern
  disc(darkParts, [0, 1.85, 0.5], 'y', 0.45, 0.2, 12);

  // Kufen
  for (const side of [1, -1]) {
    box(body, side * 1.05, -1.15, 0.7, 0.14, 0.14, 3.6);
    box(body, side * 0.7, -0.75, 1.6, 0.1, 0.75, 0.1);
    box(body, side * 0.7, -0.75, -0.5, 0.1, 0.75, 0.1);
  }

  return { body, darkParts, glass, rotor };
}

/**
 * Triebwerks-Loft seitlich versetzen können wir im einfachen loft() nicht —
 * deshalb hier ein kleiner Nachbearbeitungsschritt: verschiebt die zuletzt
 * hinzugefügten `vertexCount` Vertices einer Geometrie um (dx, dy, dz).
 */
function translateLast(geo, vertexCount, dx, dy, dz) {
  const start = geo.positions.length - vertexCount * 3;
  for (let i = start; i < geo.positions.length; i += 3) {
    geo.positions[i] += dx;
    geo.positions[i + 1] += dy;
    geo.positions[i + 2] += dz;
  }
}

/**
 * Erzeugt alle fünf Modelle und liefert { airplane, jet, helicopter,
 * smallplane, glider } als Blob-URLs für Cesium ModelGraphics.
 */
export function createAircraftModelUris() {
  // Airliner: Triebwerke nachträglich seitlich unter die Flügel schieben
  const airliner = buildAirliner();
  // Nacelles hell (wie der Rumpf), Fan-Scheiben vorn dunkel, plus Pylon
  const engines = new Geo();
  const engineDark = new Geo();
  for (const side of [1, -1]) {
    const before = engines.positions.length / 3;
    loft(engines, [
      { z: 5.4, cy: -1.7, rx: 1.05, ry: 1.05, n: 12 },
      { z: 3.6, cy: -1.7, rx: 1.02, ry: 1.02, n: 12 },
      { z: 1.4, cy: -1.7, rx: 0.8, ry: 0.8, n: 12 }
    ]);
    translateLast(engines, engines.positions.length / 3 - before, side * 5.6, 0, 0);
    // Fan-Scheibe im Einlauf + Abgaskonus
    disc(engineDark, [side * 5.6, -1.7, 5.32], 'z', 0.92, 0.12, 16);
    const coneBefore = engineDark.positions.length / 3;
    loft(engineDark, [
      { z: 1.5, cy: -1.7, rx: 0.5, ry: 0.5, n: 10 },
      { z: 0.5, cy: -1.7, rx: 0.12, ry: 0.12, n: 10 }
    ]);
    translateLast(engineDark, engineDark.positions.length / 3 - coneBefore, side * 5.6, 0, 0);
    // Pylon Flügel ↔ Triebwerk
    box(engines, side * 5.6, -0.75, 2.9, 0.4, 0.9, 2.4);
  }

  const airplane = buildGltf([
    { geo: airliner.body, mat: MAT.body },
    { geo: engines, mat: MAT.body },
    { geo: engineDark, mat: MAT.dark },
    { geo: airliner.glass, mat: MAT.glass }
  ]);

  const biz = buildBizjet();
  const jet = buildGltf([
    { geo: biz.body, mat: MAT.body },
    { geo: biz.darkParts, mat: MAT.dark },
    { geo: biz.glass, mat: MAT.glass }
  ]);

  const small = buildSmallPlane();
  const smallplane = buildGltf([
    { geo: small.body, mat: MAT.body },
    { geo: small.darkParts, mat: MAT.dark },
    { geo: small.glass, mat: MAT.glass }
  ]);

  const heli = buildHelicopter();
  const helicopter = buildGltf([
    { geo: heli.body, mat: MAT.body },
    { geo: heli.darkParts, mat: MAT.dark },
    { geo: heli.glass, mat: MAT.glass },
    { geo: heli.rotor, mat: MAT.rotor }
  ]);

  const sail = buildGlider();
  const glider = buildGltf([
    { geo: sail.body, mat: MAT.body },
    { geo: sail.glass, mat: MAT.glass }
  ]);

  return { airplane, jet, helicopter, smallplane, glider };
}
